import React, { useEffect } from 'react';
import styles from './index.module.less';
import classNames from 'classnames';
import { AppSettings } from '@/shared/app-common';
import { CommonBackContent } from '@/components/Layouts/CommonBackContent';
import { useConfigProvider } from '@/context/ConfigProvider';
import { useRouter } from 'next/router';

const data: {
  title: string;
  date: string;
  desc: string[];
  list: {
    title?: string;
    list: {
      title?: string;
      list: string[];
    }[];
  }[];
} = {
  title: 'Cookie Policy',
  date: 'Effective Date: May 24th, 2024',
  desc: [
    `This Cookie Policy explains how Cyber Eel Tech Inc. and its subsidiaries and affiliates (collectively “DolphinRadar”, “us” or “we”) use cookies, web beacons, tracking pixels, clear gifs and other similar tracking technologies (collectively "Cookies") when you visit websites owned and operated by DolphinRadar that link to this Cookie Policy (each a “Website” and collectively “Websites”). It explains what these technologies are and why we use them, as well as how to control our use of them.`,
    `This Cookie Policy forms part of our Privacy Policy, which sets out how and why we collect, store, use and share personal data generally, as well as your rights in relation to your personal data. Please read our Privacy Policy for information on these items. `,
    `By choosing to use the Website you acknowledge that we may set strictly necessary Cookies (described below) on your computer or other device to operate and administer the Website. Further, where you give permission, we may also set certain Cookies that are not strictly necessary, described below. If you have any questions about this Cookie Policy or our Privacy Policy, please emali us: info.dolphinradar@gmail.com.`,
  ],
  list: [
    {
      title: `What are Cookies?`,
      list: [
        {
          list: [
            `Cookies are small pieces of information (text files) that websites place and store on your computer, mobile phone or any other device that can access the Internet when you visit a website. The next time you visit the website, Cookies allow that website to recognize your browser. Cookies may store user preferences and other information, such as a user’s login details. Cookies may also be used to collect statistics on users’ use of a website or to ensure that certain graphic elements are correctly displayed and all functionalities of the website work properly. `,
            `Cookies can remain on your computer or mobile device for various periods of time. Some Cookies are 'session Cookies', meaning that they are stored temporarily on your computer or other device only while your browser is open. These are deleted automatically from your computer or other device once you close your browser.`,
            `Other Cookies are 'persistent Cookies', meaning that they remain on your computer or other device after you close your browser. They can be used by websites to recognize your computer when you open your browser and browse the Internet again. `,
            `Cookies set by the website owner (in this case, DolphinRadar) are called “first party Cookies”. Cookies set by parties other than the website owner are called “third party Cookies”.  Third party Cookies enable third party features or functionality to be provided on or through the website (for example, advertising, interactive content and analytics). The parties that set these third-party Cookies can recognize your computer both when you visit the website in question and also when you visit certain other websites.`,
          ],
        },
      ],
    },
    {
      title: `What does DolphinRadar use Cookies for?`,
      list: [
        {
          list: [
            `
            We use first party and third-party Cookies for several reasons. Some Cookies are required for technical reasons in order for our Website to operate, and we refer to these as “essential” or “strictly necessary” Cookies. Other Cookies help us to provide you with a better Website by enabling us to monitor the pages that you find useful and tailor our Website to your needs, likes and dislikes by gathering and remembering information about your preferences.
            `,
            `
            DolphinRadar use Cookies for the following purposes:
            `,
            `
            ● to provide you with our Website services, allowing you to adapt the content or display of our services to your device to improve your navigation and optimize our Website;
            `,
            `
            ● to store information relating to a form already completed (e.g. your user account) or relating to products, services or information of our services (e.g. contents consulted, etc.);
            `,
            `
            ● to customize our content and advertising according to your interests;
            `,
            `
            ● to anonymize your personal information for statistical and analytical purposes;
            `,
            `
            ● to gather statistics and audience measurements on the use of our Website;
            `,
            `
            ● to manage the security of our services by detecting/tracing any attempt of malice, computer intrusion or violation of the general conditions of use;`,
            `
            ● to fulfill our legal obligations, respond to any requests of third parties empowered by law to ask us to communicate navigation data and/or personal data of users of our services.`,
            `The types of Cookies used on the Website are described in more detail below.`,
          ],
        },
      ],
    },
    {
      title: `What types of Cookies do we use?`,
      list: [
        {
          list: [
            `
            The Website uses different types of Cookies as set out below.
            `,
          ],
        },
        {
          title: `1.Strictly Necessary Cookies`,
          list: [
            `These Cookies are necessary to ensure the proper functioning of the Websites and to provide you with services available through our Website. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences, logging in or filling in forms. The information collected through these Cookies is not used for marketing purposes. As these Cookies are necessary for the functionality of our Website, they cannot be turned off. You can set your browser to block or alert you about these Cookies (for more information, please read “How to control or delete cookies”), but that will cause some parts of the Website to not work. `,
          ],
        },
        {
          title: `2.Functionality Cookies`,
          list: [
            `Functionality Cookies allow the Website to remember the user’s preferences and choices they make on the Website including username, region, and language. This allows the Website to provide personalized features. They are anonymous and do not track browsing activity across other websites. Functionality cookies can include first party, third party, persistent or session cookies.`,
          ],
        },
        {
          title: `3.Performance/Analytics Cookies`,
          list: [
            `We use performance/analytics Cookies to analyze how the Website is accessed, used, or is performing in order to provide you with a better user experience and to maintain, operate and continually improve the Website. For example, these Cookies allow us to:`,
            `a. Better understand our Website visitors so that we can improve how we present our content;`,
            `b. Test different design ideas for particular pages, such as our homepage;`,
            `c. Collect information about Website visitors such as where they are located and what browsers they are using;`,
            `d. Determine the number of unique users of the Website;`,
            `e. Improve the Website by measuring any errors that occur; and`,
            `f. Conduct research and diagnostics to improve product offerings.`,
          ],
        },
        {
          title: `4.Marketing Cookies`,
          list: [
            `These Cookies allow us to provide advertising messages to visitors on third party websites and measure the effectiveness of our marketing initiatives. We adhere to industry accepted best practice in respect of our use of marketing Cookies.`,
          ],
        },
      ],
    },
    {
      title: `How to control or delete cookies`,
      list: [
        {
          list: [
            `Browser Controls: You can change your browser's settings to delete Cookies that have already been set and to not accept new Cookies. To learn more about how to do this, visit the help pages of your browser:`,
            `● Chrome (https://support.google.com/chrome/answer/95647?hl=en);`,
            `● Firefox (https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer);`,
            `● Internet Explorer (https://support.microsoft.com/en-us/help/17442/windows-internet-explorer-delete-manage-cookies#ie=ie-11);`,
            `● Opera (https://help.opera.com/en/latest/web-preferences/);`,
            `● Safari (https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac).`,
            `If you wish to find out more about removing or blocking cookies, you may visit: http://www.aboutcookies.org/Default.aspx?page=2.`,
            `Disabling Most Interest Based Advertising: Moreover, most advertising networks offer you a way to opt out of targeted advertising. If you would like to find out more information, please visit http://www.aboutads.info/choices/ or http://www.youronlinechoices.com (or if located in the European Union or the United Kingdom, by clicking here: http://www.youronlinechoices.eu/). Please note this does not opt you out of being served advertising. You will continue to receive generic advertisements.`,
            `Please note, however, that if you delete Cookies or do not accept them, you might not be able to use all or part of the features we offer, you may not be able to store your preferences, and some of our pages might not display properly.`,
            `With new devices being created all the time, including tablets and phones, it is not possible to list every browser for every device. The best advice is to consult the manufacturer's website for further instructions regarding Cookies.
`,
          ],
        },
      ],
    },
    {
      title: `Cookies that have been set in the past`,
      list: [
        {
          list: [
            `If you have disabled one or more Cookies, we may still use information collected from Cookies prior to your preference change, however, we will stop using the disabled Cookie to collect any further information.`,
          ],
        },
      ],
    },
    {
      title: `What about other tracking technologies, like pixel tags?`,
      list: [
        {
          list: [
            `
            Cookies are not the only way to recognize or track visitors to a website.  We may use other, similar technologies from time to time, called pixel tags (or web beacons or clear gifs). These are tiny graphics files that contain a unique identifier permitting us to recognize when someone has visited our Websites and track the online movements of Website users. This helps us better manage the Website by informing us what content is effective and improve the effectiveness of our marketing campaigns. In contrast to cookies, which are stored on a user's computer hard drive, pixels are embedded on Website pages. In many instances, these technologies are reliant on cookies to function properly, and so declining cookies will impair their functioning.
            `,
          ],
        },
      ],
    },
    {
      title: `Third Party Website Cookies`,
      list: [
        {
          list: [
            `
            When using our Website, you may be directed to other websites ("Third Party Sites") for such activities as surveys, for job applications, and to view content hosted on those sites such as an embedded video or news article. The Third Party Sites may use their own cookies or similar technologies. We do not have control over the placement of cookies or other technologies by Third Party Sites you visit, even if you are directed to them from our Website. The information that you share with Third Party Sites will be governed by the specific privacy policies, cookie policies and terms of service of the Third Party Sites and not by this Cookie Policy.
            `,
          ],
        },
      ],
    },
    {
      title: `Updates to this Cookies Policy`,
      list: [
        {
          list: [
            `This Cookies Policy may be updated regularly as and when new Cookies are implemented, or existing Cookies removed. If necessary, we will draw the changes to your attention when you next visit the Website.
            `,
            `We may also update this Cookie Policy based upon Cookie best practices and legal requirements.`,
            `You are encouraged to check back here in order to keep up-to-date with the latest version of this Cookie Policy.
            `,
          ],
        },
      ],
    },
  ],
};

const CookiePolicyContent = () => {
  // const { isBlogWeb } = useConfigProvider();
  // const router = useRouter();
  // useEffect(() => {
  //   if (isBlogWeb) {
  //     router.push('/instagram-tracking-blog');
  //   }
  // }, []);
  // if (isBlogWeb) {
  //   return <></>;
  // }

  return (
    <CommonBackContent style={{ minHeight: '100vh' }}>
      <div className={styles.homeContent}>
        <h1>{data.title}</h1>
        <span>{data.date}</span>
        <div className={styles.desc} dangerouslySetInnerHTML={{ __html: data.desc }} />
        {data.list.map((item, index) => {
          return (
            <div key={index + item.title}>
              <h2>{item.title}</h2>
              {item.list && (
                <>
                  {item.list.map((subItem, subIndex) => {
                    return (
                      <div key={subIndex}>
                        {subItem?.title && <h3>{subItem?.title}</h3>}
                        {subItem.list.map((str, strIndex) => {
                          return <p key={strIndex} dangerouslySetInnerHTML={{ __html: str }} />;
                        })}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          );
        })}
      </div>
    </CommonBackContent>
  );
};

export default CookiePolicyContent;
